/**
 * Consistency chip for the Data Dictionary card (inc5 step E) — a VIEWER of
 * pipelineVerification/latest, in the postgen-chip tradition: honest about
 * the last recorded run, never a claim about the current instant (the
 * download gate re-verifies server-side at click time; this chip only
 * orients). Renders nothing when no run exists.
 *
 * Copy rules: plain language, no raw check ids, never "AI".
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { db } from '../../firebase';

interface LatestRunLike {
  status?: string;
  findingsCount?: number;
  correctedCount?: number;
  completedAt?: { toDate?: () => Date };
}

export default function ConsistencyChip({ projectId }: { projectId: string }) {
  const [latest, setLatest] = useState<LatestRunLike | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'pipelineVerification', 'latest'),
      (snap) => setLatest(snap.exists() ? (snap.data() as LatestRunLike) : null),
      () => setLatest(null),
    );
    return () => unsub();
  }, [projectId]);

  if (!latest?.status) return null;

  const when = latest.completedAt?.toDate?.();
  const title = when ? `Last consistency check: ${when.toLocaleString()}` : undefined;

  if (latest.status === 'clean') {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
        title={title}
      >
        <CheckCircle2 className="h-3 w-3" />
        Documents agree
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
      title={title}
    >
      <AlertTriangle className="h-3 w-3" />
      Differences found
    </span>
  );
}
